import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DoorOpen, DoorClosed, Wrench, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, DataTable, StatusBadge, Sel, BranchSel, Dot, opts } from "@/components/kit";
import OverrideDialog from "@/components/OverrideDialog";
import DeviceLogs from "@/components/DeviceLogs";
import { api, useApi, fmtErr, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function BarrierCard({ b, onOverride, onLogs, reload }) {
  const { can, branchName } = useAuth();
  const state = !b.enabled ? "disabled" : b.status === "online" ? b.barrier_state : b.status;
  const cmd = b.last_open_command;
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className={`rounded-lg border bg-card p-4 transition-transform hover:-translate-y-0.5 ${["fault", "offline"].includes(b.status) ? "border-red-500/60" : "border-border/60"}`} data-testid={`barrier-card-${b.device_code}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2"><Dot ok={b.status === "online"} /><span className="font-mono text-sm font-bold">{b.device_code}</span></div>
          <div className="mt-0.5 text-xs text-muted-foreground">{branchName(b.branch_id)} • {b.lane_name} • {label(b.direction)}</div>
        </div>
        <StatusBadge status={state} testid={`barrier-state-${b.device_code}`} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <span className="text-muted-foreground">Heartbeat</span><span className="font-mono">{dt(b.last_heartbeat)}</span>
        <span className="text-muted-foreground">IP / API</span><span className="truncate font-mono">{b.ip_address}</span>
        <span className="text-muted-foreground">Last open</span><span className="truncate font-mono">{cmd ? `${dt(cmd.at)} ${cmd.manual ? "(MANUAL)" : ""}` : "—"}</span>
        <span className="text-muted-foreground">By / plate</span><span className="truncate">{cmd ? `${cmd.by} • ${cmd.plate || "—"}` : "—"}</span>
        <span className="text-muted-foreground">Payment</span><span>{cmd?.payment_status ? label(cmd.payment_status) : "—"}</span>
        <span className="text-muted-foreground">Response</span><span className="truncate font-mono">{b.last_command_response || "—"}</span>
        <span className="text-muted-foreground">Maintenance</span><span className="font-mono">{(b.last_maintenance || "").slice(0, 10)} → {(b.next_maintenance || "").slice(0, 10)}</span>
      </div>
      {b.fault && <div className="mt-2 rounded bg-red-500/10 px-2 py-1 text-xs text-red-500">Fault: {b.fault}</div>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {can("override") && <Button size="sm" variant="outline" className="border-red-500/50 text-red-500" data-testid={`barrier-override-${b.device_code}`} onClick={() => onOverride(b)}><DoorOpen className="h-3.5 w-3.5" /> Override open</Button>}
        {can("control_barrier") && <Button size="sm" variant="outline" data-testid={`barrier-close-${b.device_code}`} onClick={() => act(() => api.post(`/barriers/${b.id}/command`, { action: "close" }), "Close command sent")}><DoorClosed className="h-3.5 w-3.5" /> Close</Button>}
        {can("manage_devices") && <Button size="sm" variant="ghost" data-testid={`barrier-maint-${b.device_code}`} onClick={() => act(() => api.put(`/devices/${b.id}`, { status: b.status === "maintenance" ? "online" : "maintenance" }), "Mode updated")}><Wrench className="h-3.5 w-3.5" /> {b.status === "maintenance" ? "Exit maint." : "Maint."}</Button>}
        <Button size="sm" variant="ghost" data-testid={`barrier-logs-${b.device_code}`} onClick={() => onLogs(b.id)}><History className="h-3.5 w-3.5" /> Logs</Button>
      </div>
    </div>
  );
}

function Overrides() {
  const { branch, can, branchName } = useAuth();
  const [status, setStatus] = useState("");
  const { data, loading, reload } = useApi("/overrides", { branch_id: branch, status });
  const act = async (o, kind) => {
    const comments = kind === "approve" ? window.prompt("Approval comments (optional)") ?? "" : window.prompt(`${kind} comments`);
    if (comments === null) return;
    try {
      const r = await api.post(`/overrides/${o.id}/${kind}`, { comments });
      toast.success(kind === "approve" ? `Approved — barrier ${r.data.barrier_response}` : `Override ${kind}ed`);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <>
      <div className="mb-3"><Sel value={status} onChange={setStatus} testid="overrides-status-filter" allLabel="All statuses" options={opts(["pending_approval", "escalated", "approved", "executed", "failed", "rejected"])} /></div>
      <DataTable testid="overrides-table" loading={loading} rows={data} columns={[
        { key: "override_no", label: "No.", render: (r) => <span className="font-mono text-xs">{r.override_no}</span> },
        { key: "created_at", label: "Date/time", render: (r) => dt(r.created_at, { second: "2-digit" }) },
        { key: "b", label: "Branch / lane", render: (r) => `${branchName(r.branch_id)} • ${r.lane_name}` },
        { key: "plate", label: "Plate", render: (r) => (r.plate ? <span className="plate text-xs">{r.plate}</span> : "—") },
        { key: "reason", label: "Reason", render: (r) => label(r.reason) },
        { key: "payment_status", label: "Payment", render: (r) => <StatusBadge status={r.payment_status} /> },
        { key: "requested_by", label: "Requested by" },
        { key: "approver", label: "Approver" },
        { key: "status", label: "Status", render: (r) => <span className="flex gap-1"><StatusBadge status={r.status} />{r.escalated && <StatusBadge status="escalated" />}</span> },
        { key: "act", label: "", render: (r) => ["pending_approval", "escalated"].includes(r.status) && can("approve") && (
          <span className="flex gap-2 text-xs">
            <button className="text-emerald-500 underline" data-testid={`override-approve-${r.override_no}`} onClick={() => act(r, "approve")}>Approve</button>
            <button className="text-red-500 underline" data-testid={`override-reject-${r.override_no}`} onClick={() => act(r, "reject")}>Reject</button>
          </span>
        ) },
      ]} />
    </>
  );
}

export default function Barriers() {
  const { branch, setBranch } = useAuth();
  const { data, reload } = useApi("/devices", { type: "barrier", branch_id: branch });
  const [ovr, setOvr] = useState(null);
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);
  return (
    <div className="fade-up">
      <PageHeader title="Barrier Management" subtitle="Every entry/exit barrier linked to ParkingCore • escalation: Branch Manager → Head Office Accounting → IT → Super Admin" testid="barriers-header">
        <BranchSel value={branch} onChange={setBranch} testid="barriers-branch" />
      </PageHeader>
      <Tabs defaultValue="grid">
        <TabsList>
          <TabsTrigger value="grid" data-testid="tab-barriers">Barriers ({data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="overrides" data-testid="tab-overrides">Manual overrides</TabsTrigger>
        </TabsList>
        <TabsContent value="grid" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger">
            {data?.map((b) => <BarrierCard key={b.id} b={b} onOverride={setOvr} onLogs={setLogs} reload={reload} />)}
          </div>
        </TabsContent>
        <TabsContent value="overrides" className="mt-4"><Overrides /></TabsContent>
      </Tabs>
      {ovr && <OverrideDialog open={!!ovr} onOpenChange={() => setOvr(null)} barrierId={ovr.id} onDone={reload} />}
      <DeviceLogs id={logs} onClose={() => setLogs(null)} />
    </div>
  );
}
